import React, { useEffect, useState, useMemo } from "react";
import {
  Stack,
  Divider,
  Typography,
  Box,
  IconButton,
  CircularProgress,
} from "@mui/material";
import { Formik, Form, FieldArray, FormikProps } from "formik";
import * as Yup from "yup";
import axios from "axios";

import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutlineOutlined";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import DeleteIcon from "@mui/icons-material/Delete";

import FormAlert from "../components/common/FormAlert";
import FormTextField from "../components/common/FormTextField";
import FormSelectField from "../components/common/FormSelectField";
import FormSearchSelect from "../components/common/FormSearchSelect";
import SubmitButton from "../components/buttons/SubmitButton";

import { useAppDispatch, useAppSelector } from "../redux/store";
import useDebounce from "../hooks/useDebounce";
import { cleanFormData } from "../helpers/formHelpers";
import { itemActions } from "../redux/slices/item";

import { createItem, updateItem } from "../redux/actions/itemActions";
import { getCategories } from "../redux/actions/categoryActions";
import { getBrands } from "../redux/actions/brandActions";
import { getTaxCodes } from "../redux/actions/taxCodeActions";
import { getQuantityUnits } from "../redux/actions/quantityUnitActions";
import { getPackageUnits } from "../redux/actions/packageUnitActions";

import { ItemType } from "../shared/enums/ItemType";
import { ItemRequestDto } from "../shared/dtos/item/ItemRequestDto";
import { ItemResponseDto } from "../shared/dtos/item/ItemResponseDto";

interface CreateOrUpdateItemFormProps {
  item?: ItemResponseDto | null;
  isEditing?: boolean;
  loading: boolean;
  error: string | null;
}

const ITEM_TYPES = [
  { value: ItemType.PRODUCT, label: "Product (Tangible)" },
  { value: ItemType.RAW_MATERIAL, label: "Raw Material (Tangible)" },
  { value: ItemType.SERVICE, label: "Service (Intangible)" },
];

// Strictly typed Formik values
interface ItemFormValues {
  name: string;
  sku: string | null;
  itemType: ItemType;
  description: string;
  buyingPrice: number | "";
  retailPrice: number | "";
  wholesalePrice: number | "";
  lowStockThreshold: number | "";
  categoryId: string | "";
  brandId: string | "";
  taxCodeId: string | "";
  quantityUnitId: string | "";
  packageUnitId: string | "";
  initialStock: number | "";
  images: string[];
}

// Advanced Yup schema handling empty strings -> numbers
const getItemSchema = (isEditing: boolean): Yup.ObjectSchema<ItemFormValues> =>
  Yup.object().shape({
    name: Yup.string().required("Item name is required"),
    sku: Yup.string().nullable().notRequired(),
    itemType: Yup.mixed<ItemType>()
      .oneOf(Object.values(ItemType))
      .required("Required"),
    description: Yup.string().notRequired(),

    buyingPrice: Yup.number()
      .transform((v, o) => (o === "" ? undefined : v))
      .required("Required")
      .min(0, "Cannot be negative"),
    retailPrice: Yup.number()
      .transform((v, o) => (o === "" ? undefined : v))
      .required("Required")
      .min(0, "Cannot be negative"),
    wholesalePrice: Yup.number()
      .transform((v, o) => (o === "" ? null : v))
      .nullable()
      .min(0),
    lowStockThreshold: Yup.number()
      .transform((v, o) => (o === "" ? null : v))
      .nullable()
      .integer()
      .min(0),

    categoryId: Yup.string().required("Required"),
    brandId: Yup.string().required("Required"),
    taxCodeId: Yup.string().required("Required"),
    quantityUnitId: Yup.string().required("Required"),
    packageUnitId: Yup.string().required("Required"),

    initialStock: Yup.number().when("itemType", {
      is: ItemType.PRODUCT,
      then: (schema) =>
        isEditing
          ? schema.nullable().notRequired()
          : schema
              .transform((v, o) => (o === "" ? undefined : v))
              .required("Opening stock is required")
              .min(0),
      otherwise: (schema) => schema.nullable().notRequired(),
    }),

    images: Yup.array().of(Yup.string().required()).defined(),
  }) as Yup.ObjectSchema<ItemFormValues>;

const CreateOrUpdateItemForm: React.FC<CreateOrUpdateItemFormProps> = ({
  item = null,
  isEditing = false,
  loading = false,
  error = null,
}) => {
  const dispatch = useAppDispatch();
  const [imageLoading, setImageLoading] = useState<boolean>(false);

  // --- Dynamic Search States ---
  const [catSearch, setCatSearch] = useState<string>("");
  const [brandSearch, setBrandSearch] = useState<string>("");
  const [taxSearch, setTaxSearch] = useState<string>("");
  const [qtySearch, setQtySearch] = useState<string>("");
  const [pkgSearch, setPkgSearch] = useState<string>("");

  const debouncedCatSearch = useDebounce(catSearch, 500);
  const debouncedBrandSearch = useDebounce(brandSearch, 500);
  const debouncedTaxSearch = useDebounce(taxSearch, 500);
  const debouncedQtySearch = useDebounce(qtySearch, 500);
  const debouncedPkgSearch = useDebounce(pkgSearch, 500);

  // --- Redux Selectors ---
  const { items: categories, loadingFetchAll: loadingCat } = useAppSelector(
    (state) => state.category,
  );
  const { items: brands, loadingFetchAll: loadingBrand } = useAppSelector(
    (state) => state.brand,
  );
  const { items: taxCodes, loadingFetchAll: loadingTax } = useAppSelector(
    (state) => state.taxCode,
  );
  const { items: quantityUnits, loadingFetchAll: loadingQty } = useAppSelector(
    (state) => state.quantityUnit,
  );
  const { items: packageUnits, loadingFetchAll: loadingPkg } = useAppSelector(
    (state) => state.packageUnit,
  );
  const { userDefaults } = useAppSelector((state) => state.settings);

  // --- Initial Data Load ---
  useEffect(() => {
    if (!categories?.length) dispatch(getCategories({ size: 100 }));
    if (!brands?.length) dispatch(getBrands({ size: 100 }));
    if (!taxCodes?.length) dispatch(getTaxCodes({ size: 100 }));
    if (!quantityUnits?.length) dispatch(getQuantityUnits({ size: 100 }));
    if (!packageUnits?.length) dispatch(getPackageUnits({ size: 100 }));
  }, [
    dispatch,
    categories?.length,
    brands?.length,
    taxCodes?.length,
    quantityUnits?.length,
    packageUnits?.length,
  ]);

  // --- Backend Searching via Debounce ---
  useEffect(() => {
    if (debouncedCatSearch)
      dispatch(getCategories({ search: debouncedCatSearch, size: 20 }));
  }, [dispatch, debouncedCatSearch]);

  useEffect(() => {
    if (debouncedBrandSearch)
      dispatch(getBrands({ search: debouncedBrandSearch, size: 20 }));
  }, [dispatch, debouncedBrandSearch]);

  useEffect(() => {
    if (debouncedTaxSearch)
      dispatch(getTaxCodes({ search: debouncedTaxSearch, size: 20 }));
  }, [dispatch, debouncedTaxSearch]);

  useEffect(() => {
    if (debouncedQtySearch)
      dispatch(getQuantityUnits({ search: debouncedQtySearch, size: 20 }));
  }, [dispatch, debouncedQtySearch]);

  useEffect(() => {
    if (debouncedPkgSearch)
      dispatch(getPackageUnits({ search: debouncedPkgSearch, size: 20 }));
  }, [dispatch, debouncedPkgSearch]);

  // --- Image Parsing Helper ---
  const parseImages = (imagesData: unknown): string[] => {
    if (!imagesData) return [];
    if (Array.isArray(imagesData)) return imagesData;
    if (typeof imagesData === "string") {
      try {
        const parsed = JSON.parse(imagesData);
        return Array.isArray(parsed) ? parsed : [];
      } catch {
        return [];
      }
    }
    return [];
  };

  // --- Initial Values & Defaults Integration ---
  const initialValues: Partial<ItemRequestDto> = useMemo(
    () => ({
      name: item?.name || "",
      sku: item?.sku || "",
      itemType:
        item?.itemType ||
        (userDefaults?.itemType as ItemType) ||
        ItemType.PRODUCT,
      description: item?.description || "",
      buyingPrice: item?.buyingPrice ?? ("" as unknown as number),
      retailPrice: item?.retailPrice ?? ("" as unknown as number),
      wholesalePrice: item?.wholesalePrice ?? ("" as unknown as number),
      lowStockThreshold: item?.lowStockThreshold ?? ("" as unknown as number),
      categoryId: item?.categoryId || userDefaults?.categoryId || "",
      brandId: item?.brandId || userDefaults?.brandId || "",
      taxCodeId: item?.taxCodeId || userDefaults?.taxCode || "",
      quantityUnitId: item?.quantityUnitId || userDefaults?.quantityUnit || "",
      packageUnitId: item?.packageUnitId || userDefaults?.packageUnit || "",
      initialStock: "" as unknown as number,
      images: parseImages(item?.images),
    }),
    [item, userDefaults],
  );

  // Web Browser Image Upload using Axios to Cloudinary
  const handleImageUpload = async (
    file: File,
    pushImage: (url: string) => void,
  ): Promise<void> => {
    if (!file) return;
    setImageLoading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("upload_preset", "GN_Products");

      const { data } = await axios.post<{ secure_url?: string; url?: string }>(
        "https://api.cloudinary.com/v1_1/dsiyinydr/image/upload",
        formData,
      );

      if (data?.secure_url || data?.url) {
        pushImage(data.secure_url || data.url!);
      }
    } catch (err) {
      console.error("Cloudinary upload failed", err);
    } finally {
      setImageLoading(false);
    }
  };

  return (
    <Box sx={{ width: "100%" }}>
      <FormAlert
        message={error}
        severity="error"
        closable
        onClose={() =>
          dispatch(
            isEditing
              ? itemActions.setErrorUpdate(null)
              : itemActions.setErrorCreate(null),
          )
        }
      />
      <Formik
        enableReinitialize
        initialValues={initialValues}
        validationSchema={getItemSchema(isEditing)}
        onSubmit={(values) => {
          const payload = cleanFormData(
            values as unknown as Record<string, unknown>,
          ) as unknown as ItemRequestDto;

          // Ensure proper number casting before dispatch
          payload.buyingPrice = Number(payload.buyingPrice);
          payload.retailPrice = Number(payload.retailPrice);
          if (payload.wholesalePrice)
            payload.wholesalePrice = Number(payload.wholesalePrice);
          if (payload.lowStockThreshold)
            payload.lowStockThreshold = Number(payload.lowStockThreshold);

          if (isEditing && item) {
            delete payload.initialStock;
            dispatch(updateItem({ id: item.id, values: payload }));
          } else {
            if (payload.initialStock)
              payload.initialStock = Number(payload.initialStock);
            dispatch(createItem(payload));
          }
        }}
      >
        {({ dirty, isValid, values }: FormikProps<Partial<ItemRequestDto>>) => (
          <Form>
            <Stack spacing={3}>
              <Box>
                <Typography
                  variant="overline"
                  sx={{ fontWeight: 700, color: "text.secondary" }}
                >
                  Basic Info
                </Typography>
                <Box
                  sx={{
                    display: "grid",
                    gap: 2,
                    mt: 1,
                    gridTemplateColumns: { xs: "1fr", sm: "2fr 1fr 1fr" },
                  }}
                >
                  <FormTextField name="name" label="Item Name" />
                  <FormTextField name="sku" label="SKU" />
                  <FormSelectField
                    name="itemType"
                    label="Item Type"
                    options={ITEM_TYPES}
                  />
                </Box>
                <Box sx={{ mt: 2 }}>
                  <FormTextField
                    name="description"
                    label="Description"
                    multiline
                    rows={2}
                  />
                </Box>
              </Box>

              <Divider />

              <Box>
                <Typography
                  variant="overline"
                  sx={{ fontWeight: 700, color: "text.secondary" }}
                >
                  Pricing & Inventory
                </Typography>
                <Box
                  sx={{
                    display: "grid",
                    gap: 2,
                    mt: 1,
                    gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr 1fr" },
                  }}
                >
                  <FormTextField
                    name="buyingPrice"
                    label="Buying Price"
                    type="number"
                  />
                  <FormTextField
                    name="retailPrice"
                    label="Retail Price"
                    type="number"
                  />
                  <FormTextField
                    name="wholesalePrice"
                    label="Wholesale Price"
                    type="number"
                  />
                </Box>
                <Box
                  sx={{
                    display: "grid",
                    gap: 2,
                    mt: 2,
                    gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                  }}
                >
                  {!isEditing && values.itemType === ItemType.PRODUCT && (
                    <FormTextField
                      name="initialStock"
                      label="Opening Stock"
                      type="number"
                    />
                  )}
                  {values.itemType === ItemType.PRODUCT && (
                    <FormTextField
                      name="lowStockThreshold"
                      label="Low Stock Alert"
                      type="number"
                    />
                  )}
                </Box>
              </Box>

              <Divider />

              <Box>
                <Typography
                  variant="overline"
                  sx={{ fontWeight: 700, color: "text.secondary" }}
                >
                  Categorization
                </Typography>
                <Box
                  sx={{
                    display: "grid",
                    gap: 2,
                    mt: 1,
                    gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                  }}
                >
                  <FormSearchSelect
                    name="categoryId"
                    label="Category"
                    options={categories.map((c) => ({
                      value: c.id,
                      label: c.name,
                    }))}
                    loading={loadingCat}
                    onSearchChange={(text) => setCatSearch(text)}
                  />
                  <FormSearchSelect
                    name="brandId"
                    label="Brand"
                    options={brands.map((b) => ({
                      value: b.id,
                      label: b.name,
                    }))}
                    loading={loadingBrand}
                    onSearchChange={(text) => setBrandSearch(text)}
                  />
                </Box>
                <Box
                  sx={{
                    display: "grid",
                    gap: 2,
                    mt: 2,
                    gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr 1fr" },
                  }}
                >
                  <FormSearchSelect
                    name="taxCodeId"
                    label="Tax Code"
                    options={taxCodes.map((t) => ({
                      value: t.id,
                      label: t.name,
                    }))}
                    loading={loadingTax}
                    onSearchChange={(text) => setTaxSearch(text)}
                  />
                  <FormSearchSelect
                    name="quantityUnitId"
                    label="Qty Unit"
                    options={quantityUnits.map((q) => ({
                      value: q.id,
                      label: q.name,
                    }))}
                    loading={loadingQty}
                    onSearchChange={(text) => setQtySearch(text)}
                  />
                  <FormSearchSelect
                    name="packageUnitId"
                    label="Pkg Unit"
                    options={packageUnits.map((p) => ({
                      value: p.id,
                      label: p.name,
                    }))}
                    loading={loadingPkg}
                    onSearchChange={(text) => setPkgSearch(text)}
                  />
                </Box>
              </Box>

              <Divider />

              <FieldArray name="images">
                {({ push, remove }) => (
                  <Box>
                    <Stack
                      direction="row"
                      spacing={2}
                      sx={{ alignItems: "center", mb: 2 }}
                    >
                      <IconButton
                        component="label"
                        color="secondary"
                        disabled={
                          imageLoading || (values.images?.length || 0) >= 4
                        }
                      >
                        <UploadFileIcon fontSize="large" />
                        <input
                          type="file"
                          hidden
                          accept="image/*"
                          onChange={(e) => {
                            const f = e.target.files?.[0];
                            if (f) handleImageUpload(f, push);
                            e.target.value = "";
                          }}
                        />
                      </IconButton>
                      {imageLoading && <CircularProgress size={24} />}
                      <Typography variant="caption">
                        Images ({values.images?.length || 0}/4)
                      </Typography>
                    </Stack>
                    <Stack
                      direction="row"
                      spacing={2}
                      sx={{ flexWrap: "wrap" }}
                    >
                      {values.images?.map((img, i) => (
                        <Box
                          key={i}
                          sx={{
                            position: "relative",
                            width: 80,
                            height: 80,
                            border: "1px solid",
                            borderColor: "divider",
                            borderRadius: 1,
                          }}
                        >
                          <img
                            src={img}
                            alt="item"
                            style={{
                              width: "100%",
                              height: "100%",
                              objectFit: "cover",
                            }}
                          />
                          <IconButton
                            size="small"
                            color="error"
                            sx={{
                              position: "absolute",
                              top: -8,
                              right: -8,
                              bgcolor: "background.paper",
                            }}
                            onClick={() => remove(i)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      ))}
                    </Stack>
                  </Box>
                )}
              </FieldArray>

              <SubmitButton
                title={isEditing ? "Save Changes" : "Create Item"}
                loading={loading}
                disabled={!dirty || !isValid || loading || imageLoading}
                startIcon={
                  isEditing ? <SaveOutlinedIcon /> : <AddCircleOutlineIcon />
                }
                fullWidth
                sx={{ py: 1.5 }}
              />
            </Stack>
          </Form>
        )}
      </Formik>
    </Box>
  );
};

export default CreateOrUpdateItemForm;
